import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ErrorBoundary } from '@/components/ui/error-boundary';
import ThemeToggle from '@/components/ThemeToggle';
import SearchMetadata from '@/components/SearchMetadata';
import ApperIcon from '@/components/ApperIcon';
import { useOwnerLayoutData } from '@/hooks/useOwnerLayoutData';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { Sheet, SheetTrigger, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuLabel, DropdownMenuItem, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';

const ORDER = ['/dashboard', '/leads', '/estimates', '/proposals', '/jobs', '/production', '/follow-up', '/money', '/price-book', '/account'];

const PAGE_HINTS = {
  '/dashboard': 'Revenue opportunities and the next actions that keep work moving',
  '/leads': 'Customer details, qualification and lead status',
  '/estimates': 'Fence pricing, quotes and estimate follow-up dates',
  '/proposals': 'Proposal options and customer presentations',
  '/jobs': 'Sold work, installation dates and crew assignments',
  '/production': 'Work orders, materials and field progress',
  '/follow-up': 'Quote Rescue, calls, notes and customer drafts',
  '/money': 'Invoices, balances, payment records and proof',
  '/price-book': 'Reusable rates and quote-readiness controls',
  '/account': 'Company profile and business contact details',
};

const WORKFLOW_GROUPS = [
  { label: 'Overview', paths: ['/dashboard'] },
  { label: 'Win work', paths: ['/leads', '/estimates', '/proposals', '/follow-up'] },
  { label: 'Plan & build', paths: ['/jobs', '/production'] },
  { label: 'Billing & setup', paths: ['/money', '/price-book', '/account'] },
];

const MOBILE_DOCK_PATHS = ['/dashboard', '/jobs', '/production', '/account'];

export default function OwnerLayout() {
  const { logout, homeRoute, navItems, initials, displayName } = useOwnerLayoutData();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const items = [...navItems].sort((a, b) => {
    const rank = path => ORDER.includes(path) ? ORDER.indexOf(path) : ORDER.length;
    return rank(a.to) - rank(b.to);
  });
  // Group only the pages returned by the existing permission-aware hook.
  // Keep newly added pages discoverable even before they have a group.
  const groupedItems = [
    ...WORKFLOW_GROUPS.map(group => ({ ...group, items: items.filter(item => group.paths.includes(item.to)) })),
    { label: 'More tools', items: items.filter(item => !WORKFLOW_GROUPS.some(group => group.paths.includes(item.to))) },
  ].filter(group => group.items.length > 0);
  const currentPage = items.find(item => pathname === item.to || pathname.startsWith(item.to + '/'));
  const dockItems = MOBILE_DOCK_PATHS.map(path => items.find(item => item.to === path)).filter(Boolean);
  useEffect(() => {
    const handleShortcut = event => {
      if (!(event.ctrlKey || event.metaKey) || event.altKey || event.shiftKey || event.key.toLowerCase() !== 'k') return;
      // Do not interrupt another modal form or trap focus between dialogs.
      if (!searchOpen && document.querySelector('[role="dialog"][data-state="open"]')) return;
      event.preventDefault();
      setMenuOpen(false);
      setSearchOpen(value => !value);
    };
    document.addEventListener('keydown', handleShortcut);
    return () => document.removeEventListener('keydown', handleShortcut);
  }, [searchOpen]);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [pathname]);

  const openPage = path => {
    setSearchOpen(false);
    setMenuOpen(false);
    navigate(path);
  };

  const navigation = (mobile, pages = items) => pages.map(item => (
    <NavLink
      key={item.to}
      to={item.to}
      end={item.to === homeRoute}
      onClick={() => setMenuOpen(false)}
      className={({ isActive }) => mobile
        ? `flex min-h-11 w-full items-center gap-2 rounded-md px-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${isActive ? 'bg-primary text-primary-foreground shadow-sm' : 'text-foreground hover:bg-accent hover:text-accent-foreground'}`
        : `relative flex min-h-11 shrink-0 items-center gap-2 px-3 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring ${isActive ? 'text-sidebar-foreground after:absolute after:inset-x-3 after:-bottom-3 after:h-0.5 after:bg-primary' : 'text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground'}`
      }
    >
      <ApperIcon name={item.icon || 'Circle'} size={17} className="shrink-0" aria-hidden="true"/>
      {mobile ? <span className="min-w-0 py-2"><span className="block">{item.label}</span>{PAGE_HINTS[item.to] && <span className="block mt-0.5 text-xs font-normal leading-relaxed opacity-80">{PAGE_HINTS[item.to]}</span>}</span> : item.label}
    </NavLink>
  ));

  return (
    <div className="picketops-workspace min-h-svh bg-background text-foreground">
      <SearchMetadata/>
      <a href="#workspace-content" className="sr-only focus:not-sr-only focus:block focus:p-4 focus:bg-card">Skip to workspace content</a>
      <header className="border-b border-sidebar-border bg-sidebar text-sidebar-foreground shadow-(--shadow-sm)">
        <div className="max-w-7xl mx-auto flex min-h-20 items-center justify-between gap-3 px-4 md:px-6">
          <Link to={homeRoute} aria-label="PicketOps workspace home" className="flex items-center gap-3 rounded-lg focus-visible:ring-2 focus-visible:ring-ring">
            <span aria-hidden="true" className="hidden sm:flex size-11 items-center justify-center rounded-xl bg-primary text-primary-foreground font-heading text-2xl font-bold">P</span>
            <span>
              <span className="block font-heading text-2xl font-bold tracking-tight leading-none">PICKETOPS</span>
              <span className="hidden sm:block text-xs text-sidebar-foreground/65 mt-1">Revenue OS for fence contractors</span>
            </span>
          </Link>
          <div className="flex items-center gap-2">
            <div className="hidden sm:block"><ThemeToggle/></div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="min-h-11 gap-2 border-sidebar-border bg-sidebar-accent px-3 text-sidebar-foreground hover:bg-sidebar-accent/80 hover:text-sidebar-foreground" aria-label="Open account menu">
                  <span className="flex size-7 items-center justify-center rounded-full bg-secondary text-secondary-foreground text-xs font-bold">{initials}</span>
                  <span className="hidden lg:inline max-w-40 truncate">{displayName || 'My account'}</span>
                  <ApperIcon name="ChevronDown" size={15} aria-hidden="true"/>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel className="break-words">{displayName || 'Your workspace'}</DropdownMenuLabel>
                <DropdownMenuSeparator/>
                {items.find(item => item.to === '/account') && (
                  <DropdownMenuItem asChild className="min-h-11">
                    <Link to="/account"><ApperIcon name="Building2"/>Company profile</Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem asChild className="min-h-11">
                  <Link to="/fence-contractor-tools"><ApperIcon name="Wrench"/>Free contractor tools</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="min-h-11">
                  <Link to="/contractor-revenue-scorecard"><ApperIcon name="Gauge"/>Revenue scorecard</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="min-h-11">
                  <Link to="/fence-measurement-worksheet"><ApperIcon name="Ruler"/>Fence measurement worksheet</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="min-h-11">
                  <Link to="/fence-material-takeoff-calculator"><ApperIcon name="Boxes"/>Fence material takeoff</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="min-h-11">
                  <Link to="/fence-estimate-calculator"><ApperIcon name="Calculator"/>Fence estimate calculator</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="min-h-11">
                  <Link to="/fence-estimate-template"><ApperIcon name="FilePenLine"/>Fence estimate scope builder</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="min-h-11">
                  <Link to="/fence-labor-crew-planner"><ApperIcon name="Users"/>Fence labor & crew planner</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="min-h-11">
                  <Link to="/fence-install-schedule-planner"><ApperIcon name="CalendarDays"/>Fence install schedule planner</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="min-h-11">
                  <Link to="/fence-change-order-planner"><ApperIcon name="FileDiff"/>Fence change order planner</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="min-h-11">
                  <Link to="/fence-final-walkthrough-checklist"><ApperIcon name="ClipboardCheck"/>Final walkthrough & closeout</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="min-h-11">
                  <Link to="/fence-proposal-option-builder"><ApperIcon name="Columns3"/>Proposal option builder</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="min-h-11">
                  <Link to="/fence-profit-margin-calculator"><ApperIcon name="ShieldCheck"/>Profit Guard calculator</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="min-h-11">
                  <Link to="/fence-estimate-follow-up"><ApperIcon name="MessagesSquare"/>Quote follow-up guide</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="min-h-11">
                  <Link to="/fence-estimate-checklist"><ApperIcon name="ClipboardCheck"/>Estimate checklist</Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator/>
                <DropdownMenuItem className="min-h-11" onSelect={logout}><ApperIcon name="LogOut"/>Sign out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" className="lg:hidden min-h-11 border-sidebar-border bg-sidebar-accent text-sidebar-foreground hover:bg-sidebar-accent/80 hover:text-sidebar-foreground" aria-label="Open navigation menu"><ApperIcon name="Menu"/><span className="hidden sm:inline">Menu</span></Button>
              </SheetTrigger>
              <SheetContent side="right" className="overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>PicketOps workspace</SheetTitle>
                  <SheetDescription>Move from the first lead to the finished job without losing the revenue in between.</SheetDescription>
                </SheetHeader>
                <nav aria-label="Mobile workspace navigation" className="px-4 space-y-5 pb-5">
                  {groupedItems.map(group => (
                    <section key={group.label} aria-label={group.label}>
                      <h3 className="px-3 mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{group.label}</h3>
                      <div className="space-y-1">{navigation(true, group.items)}</div>
                    </section>
                  ))}
                </nav>
                <div className="px-4 pb-6"><ThemeToggle className="w-full min-h-11"/></div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
        <nav aria-label="Workspace navigation" className="hidden lg:flex max-w-7xl mx-auto gap-1 overflow-x-auto px-6 pb-3">{navigation(false)}</nav>
      </header>
      <div className="max-w-7xl mx-auto px-4 md:px-6 pt-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm min-w-0">
          <ApperIcon name={currentPage?.icon || 'LayoutDashboard'} size={16} className="text-primary" aria-hidden="true"/>
          <span className="text-muted-foreground">Workspace</span>
          <span className="text-muted-foreground" aria-hidden="true">/</span>
          <span className="font-semibold truncate">{currentPage?.label || 'Details'}</span>
        </div>
        <Button variant="outline" className="min-h-11 gap-3 bg-card" onClick={() => { setMenuOpen(false); setSearchOpen(true); }} aria-keyshortcuts="Control+k Meta+k">
          <ApperIcon name="Search" size={17} aria-hidden="true"/>
          Find a page
          <kbd className="hidden sm:inline rounded border border-border bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">Ctrl / ⌘ K</kbd>
        </Button>
      </div>
      <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
        <DialogContent className="p-0 overflow-hidden rounded-2xl sm:max-w-xl">
          <DialogHeader className="px-5 pt-5 pb-2 pr-12">
            <DialogTitle>Find a workspace page</DialogTitle>
            <DialogDescription>Search pages and tools. Use the arrow keys to choose and Enter to open.</DialogDescription>
          </DialogHeader>
          <Command className="rounded-none">
            <CommandInput aria-label="Search workspace pages" placeholder="Try invoices, crew, pricing or customer…" className="h-12"/>
            <CommandList className="max-h-[min(55dvh,420px)] p-2">
              <CommandEmpty>No pages match. Try a page name or a different task.</CommandEmpty>
              {groupedItems.map(group => <CommandGroup key={group.label} heading={group.label}>
                {group.items.map(item => (
                  <CommandItem key={item.to} value={item.to} keywords={[item.label, PAGE_HINTS[item.to] || '', group.label]} onSelect={() => openPage(item.to)} className="min-h-16 rounded-lg px-3 py-3 cursor-pointer">
                    <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"><ApperIcon name={item.icon || 'Circle'} size={18} aria-hidden="true"/></span>
                    <span className="min-w-0 flex-1"><span className="block font-medium">{item.label}</span><span className="block text-xs text-muted-foreground mt-1">{PAGE_HINTS[item.to] || 'Open this workspace page'}</span></span>
                    {pathname === item.to && <span className="text-xs text-muted-foreground">Current</span>}
                  </CommandItem>
                ))}
              </CommandGroup>)}
              <CommandGroup heading="Resources">
                <CommandItem value="fence-contractor-tools" keywords={['free tools', 'contractor tools', 'calculators', 'guides']} onSelect={() => openPage('/fence-contractor-tools')} className="min-h-12 rounded-lg cursor-pointer">
                  <ApperIcon name="Wrench" size={18} aria-hidden="true"/>Free fence contractor tools
                </CommandItem>
                <CommandItem value="contractor-revenue-scorecard" keywords={['revenue', 'score', 'leaks', 'assessment']} onSelect={() => openPage('/contractor-revenue-scorecard')} className="min-h-12 rounded-lg cursor-pointer">
                  <ApperIcon name="Gauge" size={18} aria-hidden="true"/>Contractor revenue scorecard
                </CommandItem>
                <CommandItem value="fence-measurement-worksheet" keywords={['measurements', 'fence runs', 'field notes', 'footage', 'measure']} onSelect={() => openPage('/fence-measurement-worksheet')} className="min-h-12 rounded-lg cursor-pointer">
                  <ApperIcon name="Ruler" size={18} aria-hidden="true"/>Fence measurement worksheet
                </CommandItem>
                <CommandItem value="fence-material-takeoff-calculator" keywords={['material takeoff', 'posts', 'rails', 'pickets', 'materials', 'quantity']} onSelect={() => openPage('/fence-material-takeoff-calculator')} className="min-h-12 rounded-lg cursor-pointer">
                  <ApperIcon name="Boxes" size={18} aria-hidden="true"/>Fence material takeoff calculator
                </CommandItem>
                <CommandItem value="fence-estimate-calculator" keywords={['estimate', 'calculator', 'footage', 'gates', 'tear out', 'quote math']} onSelect={() => openPage('/fence-estimate-calculator')} className="min-h-12 rounded-lg cursor-pointer">
                  <ApperIcon name="Calculator" size={18} aria-hidden="true"/>Fence estimate calculator
                </CommandItem>
                <CommandItem value="fence-estimate-template" keywords={['scope', 'template', 'estimate template', 'quote template', 'inclusions', 'exclusions']} onSelect={() => openPage('/fence-estimate-template')} className="min-h-12 rounded-lg cursor-pointer">
                  <ApperIcon name="FilePenLine" size={18} aria-hidden="true"/>Fence estimate scope builder
                </CommandItem>
                <CommandItem value="fence-labor-crew-planner" keywords={['labor planner', 'crew', 'crew days', 'worker hours', 'productivity', 'labor cost']} onSelect={() => openPage('/fence-labor-crew-planner')} className="min-h-12 rounded-lg cursor-pointer">
                  <ApperIcon name="Users" size={18} aria-hidden="true"/>Fence labor & crew planner
                </CommandItem>
                <CommandItem value="fence-install-schedule-planner" keywords={['install schedule', 'schedule planner', 'crew days', 'finish date', 'timeline']} onSelect={() => openPage('/fence-install-schedule-planner')} className="min-h-12 rounded-lg cursor-pointer">
                  <ApperIcon name="CalendarDays" size={18} aria-hidden="true"/>Fence installation schedule planner
                </CommandItem>
                <CommandItem value="fence-change-order-planner" keywords={['change order', 'scope change', 'credit', 'added work', 'revised contract']} onSelect={() => openPage('/fence-change-order-planner')} className="min-h-12 rounded-lg cursor-pointer">
                  <ApperIcon name="FileDiff" size={18} aria-hidden="true"/>Fence change order planner
                </CommandItem>
                <CommandItem value="fence-final-walkthrough-checklist" keywords={['final walkthrough', 'closeout', 'punch list', 'final inspection', 'customer handoff']} onSelect={() => openPage('/fence-final-walkthrough-checklist')} className="min-h-12 rounded-lg cursor-pointer">
                  <ApperIcon name="ClipboardCheck" size={18} aria-hidden="true"/>Fence final walkthrough & closeout
                </CommandItem>
                <CommandItem value="fence-proposal-option-builder" keywords={['proposal options', 'good better best', 'proposal builder', 'compare options']} onSelect={() => openPage('/fence-proposal-option-builder')} className="min-h-12 rounded-lg cursor-pointer">
                  <ApperIcon name="Columns3" size={18} aria-hidden="true"/>Fence proposal option builder
                </CommandItem>
                <CommandItem value="fence-profit-margin-calculator" keywords={['profit', 'margin', 'markup', 'pricing', 'calculator', 'profit guard']} onSelect={() => openPage('/fence-profit-margin-calculator')} className="min-h-12 rounded-lg cursor-pointer">
                  <ApperIcon name="ShieldCheck" size={18} aria-hidden="true"/>Profit Guard margin calculator
                </CommandItem>
                <CommandItem value="fence-estimate-follow-up" keywords={['quote', 'follow up', 'scripts', 'estimate', 'rescue']} onSelect={() => openPage('/fence-estimate-follow-up')} className="min-h-12 rounded-lg cursor-pointer">
                  <ApperIcon name="MessagesSquare" size={18} aria-hidden="true"/>Fence estimate follow-up guide
                </CommandItem>
                <CommandItem value="fence-estimate-checklist" keywords={['measurements', 'review', 'scope', 'checklist']} onSelect={() => openPage('/fence-estimate-checklist')} className="min-h-12 rounded-lg cursor-pointer">
                  <ApperIcon name="ClipboardCheck" size={18} aria-hidden="true"/>Fence estimate checklist
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
          <p className="border-t border-border px-5 py-3 text-xs text-muted-foreground">Page navigation only. Customer and job records are searched within their pages.</p>
        </DialogContent>
      </Dialog>
      <nav aria-label="Mobile quick navigation" className="fixed inset-x-0 bottom-[calc(6rem+env(safe-area-inset-bottom))] z-40 border-y border-border bg-background/95 shadow-[0_-8px_24px_-16px_oklch(0.1_0.02_215/0.55)] backdrop-blur lg:hidden">
        <div className="mx-auto grid max-w-xl grid-cols-4">
          {dockItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === homeRoute}
              aria-label={item.to === '/dashboard' ? 'Today — dashboard' : item.label}
              className={({ isActive }) => `flex min-h-16 flex-col items-center justify-center gap-1 px-1 py-2 text-[11px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring ${isActive ? 'text-primary' : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'}`}
            >
              <span className="relative">
                <ApperIcon name={item.icon || 'Circle'} size={20} aria-hidden="true"/>
                {pathname === item.to || pathname.startsWith(item.to + '/') ? <span aria-hidden="true" className="absolute -right-2 -top-1 size-1.5 rounded-full bg-primary"/> : null}
              </span>
              <span className="max-w-full truncate">{item.to === '/dashboard' ? 'Today' : item.label}</span>
            </NavLink>
          ))}
        </div>
      </nav>
      <main id="workspace-content" tabIndex={-1} className="min-w-0 pb-40 pt-2 md:pt-4 lg:pb-0"><ErrorBoundary key={pathname}><Outlet/></ErrorBoundary></main>
      <footer className="max-w-7xl mx-auto px-4 md:px-6 pt-6 pb-40 lg:py-6 text-xs text-muted-foreground border-t border-border">
        PicketOps · Find the leaks. Protect the margin. Recover the quotes.
      </footer>
    </div>
  );
}
